import { useState, useEffect, useCallback } from 'react';

export interface PulseEvent {
  id: string;
  name: string;
  venue: string;
  lat: number | null;
  lon: number | null;
  start_ts: number;
  end_ts: number | null;
  category: string;
  image_url: string | null;
  ticket_url: string | null;
  source: string;
  isHappening: boolean;
  startsIn: number | null; // minutes until start
}

export interface BusyZone {
  name: string;
  lat: number;
  lon: number;
  score: number;   // 0-100
  reason: string | null;
}

export function useCityPulse(userLocation?: { lat: number; lng: number } | null) {
  const [events, setEvents] = useState<PulseEvent[]>([]);
  const [busyZones, setBusyZones] = useState<BusyZone[]>([]);
  const [hasApiKeys, setHasApiKeys] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const lat = userLocation?.lat ?? 32.2226;
      const lng = userLocation?.lng ?? -110.9747;
      const [evRes, busyRes] = await Promise.all([
        fetch(`/api/pulse/events?lat=${lat}&lon=${lng}&when=both`),
        fetch(`/api/pulse/busy?lat=${lat}&lon=${lng}`),
      ]);
      if (evRes.ok) {
        const d = await evRes.json();
        setEvents(d.events || []);
        setHasApiKeys(d.hasApiKeys || false);
      }
      if (busyRes.ok) {
        const d = await busyRes.json();
        setBusyZones(d.zones || []);
      }
    } catch (e) {
      console.error('[useCityPulse]', e);
    } finally {
      setLoading(false);
    }
  }, [userLocation?.lat, userLocation?.lng]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  const happening = events.filter(e => e.isHappening);
  const upcoming  = events.filter(e => !e.isHappening);

  return { events, happening, upcoming, busyZones, hasApiKeys, loading, reload: load };
}
